import { useMemo, useState } from 'react';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import useSharedCollection from '../../firebase/useSharedCollection.js';
import searchTasks, { MIN_TERM_LENGTH } from '../../helpers/calendar/searchTasks.js';
import taskEventTitle from '../../helpers/calendar/taskEventTitle.js';

/** Meer treffers dan dit zijn geen zoekresultaat meer; dan verfijn je de term. */
const MAX_RESULTS = 12;

const DATE_FORMATTER = new Intl.DateTimeFormat('nl-NL', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
});

/**
 * De datum van een afspraak, of een woord als die er niet is.
 *
 * Een afspraak zonder datum bestaat; 'Invalid Date' in een zoekresultaat maakt
 * de hele lijst onbetrouwbaar.
 *
 * @param {*} timestamp - Firestore-Timestamp
 * @returns {string}
 */
const formatDate = (timestamp) => {
    const datum = timestamp?.toDate?.();
    if (!(datum instanceof Date) || Number.isNaN(datum.getTime())) return 'Geen datum';
    return DATE_FORMATTER.format(datum);
};

/**
 * Zoeken in de agenda, over álle afspraken van de organisatie.
 *
 * WAAROM NIET ALLEEN IN BEELD: 'een afspraak terugvinden' betekent dat je de
 * datum juist niet weet. Een filter op de zichtbare week zou vragen dat je
 * maand voor maand klikt en bij elke stap opnieuw zoekt. De grootste
 * organisatie heeft 239 afspraken, dus ze passen in één gedeelde lees.
 *
 * Een treffer aanklikken springt naar die datum en opent de afspraak: dat is
 * wat 'terugvinden' betekent — niet alleen weten dát hij bestaat.
 *
 * @param {object} props
 * @param {(task: object) => void} props.onSelect - Krijgt de gekozen afspraak
 * @returns {JSX.Element}
 */
const CalendarSearch = ({ onSelect }) => {
    const [term, setTerm] = useState('');

    // Alle afspraken van de organisatie, gedeeld met andere schermen die
    // dezelfde collectie lezen — één listener, niet één per scherm.
    const tasks = useSharedCollection('Tasks');
    const loaded = Array.isArray(tasks);

    const results = useMemo(
        () => searchTasks(loaded ? tasks : [], term),
        [tasks, loaded, term],
    );

    const zoekt = term.trim().length >= MIN_TERM_LENGTH;
    const getoond = results.slice(0, MAX_RESULTS);

    /**
     * @param {object} task - De aangeklikte afspraak
     * @returns {void}
     */
    const kies = (task) => {
        setTerm('');
        onSelect(task);
    };

    return (
        <div className="agenda-search">
            <label className="agenda-search__field">
                <SearchOutlinedIcon fontSize="small" aria-hidden="true" />
                <input
                    type="search"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Zoek een afspraak"
                    aria-label="Zoek een afspraak"
                />
            </label>

            {zoekt && (
                <div className="agenda-search__results">
                    {/* Drie takken op volgorde van zekerheid: 'weet ik nog niet'
                        is iets anders dan 'niets gevonden'. Zeggen dat er niets
                        is terwijl de afspraken nog onderweg zijn, stuurt iemand
                        onnodig weg.
                        AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
                    {!loaded ? (
                        <p className="agenda-search__message">Afspraken worden opgehaald…</p>
                    ) : getoond.length === 0 ? (
                        <p className="agenda-search__message">Geen afspraak gevonden.</p>
                    ) : (
                        <>
                            <ul className="agenda-search__list">
                                {getoond.map((task) => (
                                    <li key={task.docid || task.ID}>
                                        <button type="button" onClick={() => kies(task)}>
                                            <span className="agenda-search__title">{taskEventTitle(task)}</span>
                                            <span className="agenda-search__date">{formatDate(task.Timestamp)}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            {results.length > getoond.length && (
                                <p className="agenda-search__message">
                                    {results.length - getoond.length} treffers meer — maak de zoekterm preciezer.
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export { MAX_RESULTS, formatDate };
export default CalendarSearch;
